import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const Container = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
  position: relative;
`;

const Title = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  text-align: center;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #155e36;
  }

  svg {
    margin-right: 6px;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th,
  td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 14px 8px;
    line-height: 1.4;
  }

  th {
    background: #87ceeb;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }

  .positive {
    background-color: #e7f9ed;
    color: #2e7d32;
    font-weight: 500;
  }

  .negative {
    background-color: #ffeaea;
    color: #c62828;
    font-weight: 500;
  }

  .bold {
    font-weight: bold;
  }

  .rank-up {
    color: green;
    font-weight: bold;
  }

  .rank-down {
    color: red;
    font-weight: bold;
  }

  .rank-neutral {
    color: #666;
  }

  .highlight-top {
    background-color: rgba(0, 128, 0, 0.1);
    font-weight: bold;
  }

  .highlight-low {
    background-color: rgba(255, 0, 0, 0.1);
    font-weight: bold;
  }

  .highlight-neutral {
    background-color: #f0f0f0;
  }
`;

const TopTransactions = ({ data }) => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const exportToExcel = () => {
    const rows = filteredTransactions.map((row) => ({
      Kontrahent: row.contractor,
      "Wartość Transakcji (PLN)": row.value.toFixed(2),
      "Data Transakcji": row.date,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TopTransakcje");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `top_transakcje_${selectedYear}_${selectedMonth}.xlsx`);
  };

  const calculateTransactionValue = (row) =>
    parseFloat(
      row.K_15 ||
        row.K_19 ||
        row.K_10 ||
        row.K_21 ||
        row.K_17 ||
        row.K_11 ||
        row.K_22
    ) || 0;

  useEffect(() => {
    if (data && data.length > 0) {
      const transactionList = data.map((row) => ({
        contractor: row.NazwaKontrahenta || "Nieznany kontrahent",
        value: calculateTransactionValue(row),
        date: row.DataWystawienia || "Nieznana data",
        document: row.DowodSprzedazy || "Brak numeru",
        year: parseInt(row.Rok, 10),
        month: parseInt(row.Miesiac, 10),
      }));

      setTransactions(transactionList);

      const years = [...new Set(transactionList.map((t) => t.year))].sort(
        (a, b) => b - a
      );
      setAvailableYears(years);

      if (years.length > 0) {
        setSelectedYear(years[0]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (selectedYear) {
      const months = [
        ...new Set(
          transactions
            .filter((t) => t.year === selectedYear)
            .map((t) => t.month)
        ),
      ].sort((a, b) => a - b);

      setAvailableMonths(months);

      if (months.length > 0) {
        setSelectedMonth(months[months.length - 1]);
      }
    }
  }, [selectedYear, transactions]);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      const filtered = transactions
        .filter((t) => t.year === selectedYear && t.month === selectedMonth)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      setFilteredTransactions(filtered);
    }
  }, [selectedYear, selectedMonth, transactions]);

  if (transactions.length === 0) return <p>Ładowanie danych...</p>;

  return (
    <Container>
      <Title>
        Top 10 Największych Transakcji
        <InfoIconWrapper data-tooltip-id="top-transactions-info">
          <Info />
        </InfoIconWrapper>
      </Title>

      <InfoTooltip
        id="top-transactions-info"
        place="right"
        content={
          <>
            <strong>Największe transakcje</strong>
            <br />
            Tabela pokazuje 10 największych transakcji w wybranym okresie.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok:</label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth || ""}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
          >
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {new Date(0, month - 1).toLocaleString("pl-PL", {
                  month: "long",
                })}
              </option>
            ))}
          </Select>
        </div>

        <ExportButton onClick={exportToExcel}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </FiltersWrapper>

      <TableWrapper>
        <StyledTable>
          {/* Header row for top transactions */}
          <thead>
            <tr>
              <th>Kontrahent</th>
              <th>Wartość Transakcji</th>
              <th>Data Transakcji</th>
              <th>Nr Dokumentu</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((transaction, index) => (
              <tr key={index}>
                <td title={transaction.contractor}>
                  {transaction.contractor.length > 15
                    ? `${transaction.contractor.slice(0, 15)}...`
                    : transaction.contractor}
                </td>
                <td className="highlight">
                  {transaction.value.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td>{transaction.date}</td>
                <td>{transaction.document}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </Container>
  );
};

export default TopTransactions;
